import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { RadiusBlock } from './radiusTypes';

/* Holds the run's radius block plus which sentence popover is open.
   Open state lives here, not per-sentence, so opening one source popover
   closes any other — across the whole report, not just one paragraph. */

interface RadiusContextValue {
  block?: RadiusBlock;
  enabled: boolean;
  /** Print/PDF render: marks still show, popovers never open. */
  isPrintMode: boolean;
  openId: string | null;
  toggle: (id: string) => void;
  close: () => void;
}

const NOOP = () => undefined;

const RadiusContext = createContext<RadiusContextValue>({
  enabled: false,
  isPrintMode: false,
  openId: null,
  toggle: NOOP,
  close: NOOP,
});

export const POPOVER_ATTR = 'data-radius-pop';
export const MARK_ATTR = 'data-radius';

interface RadiusProviderProps {
  block?: RadiusBlock;
  /** Admin/dev viewer. The layer stays off for everyone else. */
  enabled?: boolean;
  isPrintMode?: boolean;
  children: React.ReactNode;
}

export const RadiusProvider: React.FC<RadiusProviderProps> = ({
  block,
  enabled = false,
  isPrintMode = false,
  children,
}) => {
  const [requestedId, setRequestedId] = useState<string | null>(null);

  const active = Boolean(enabled && block?.items && Object.keys(block.items).length > 0);

  // Derived, not synced via an effect: switching the layer off hides any open
  // popover immediately without a second render pass.
  const openId = active ? requestedId : null;

  const close = useCallback(() => setRequestedId(null), []);
  const toggle = useCallback(
    (id: string) => setRequestedId((current) => (current === id ? null : id)),
    [],
  );

  useEffect(() => {
    if (!openId) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target?.closest(`[${POPOVER_ATTR}], [${MARK_ATTR}]`)) close();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('resize', close);
    };
  }, [openId, close]);

  const value = useMemo(
    () => ({ block, enabled: active, isPrintMode, openId, toggle, close }),
    [block, active, isPrintMode, openId, toggle, close],
  );

  return <RadiusContext.Provider value={value}>{children}</RadiusContext.Provider>;
};

export const useRadius = (): RadiusContextValue => useContext(RadiusContext);
